import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import { DateTime } from 'luxon';
import React, { createContext, useContext, ReactNode } from 'react';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import {
  GetShifts,
  GetShiftsVariables,
  GetShifts_operativeViewer_staffShifts,
  StaffClockIn,
  StaffClockInVariables,
  StaffClockOut,
  StaffClockOutVariables,
} from './__queries__';
import TimeManager from '~Components/TimeManager';
import * as actions from '~actions';

const SHIFTS_QUERY = gql`
  query GetShifts($venueId: ID!) {
    operativeViewer {
      id

      staffShifts(venueId: $venueId) {
        id
        staffId
        clockedInAt
        clockedOutAt
      }
    }
  }
`;

const CLOCK_IN = gql`
  mutation StaffClockIn(
    $staffId: ID!
    $venueId: ID!
    $timestamp: String!
    $staffName: String
    $ssn: String
    $actorStaffId: ID
  ) {
    staffClockIn(
      staffId: $staffId
      venueId: $venueId
      timestamp: $timestamp
      staffName: $staffName
      ssn: $ssn
      actorStaffId: $actorStaffId
    ) {
      success
      message
      error
    }
  }
`;

const CLOCK_OUT = gql`
  mutation StaffClockOut(
    $shiftId: ID!
    $staffId: ID!
    $venueId: ID!
    $timestamp: String!
    $staffName: String
    $ssn: String
    $actorStaffId: ID
  ) {
    staffClockOut(
      shiftId: $shiftId
      staffId: $staffId
      venueId: $venueId
      timestamp: $timestamp
      staffName: $staffName
      ssn: $ssn
      actorStaffId: $actorStaffId
    ) {
      success
      message
      error
    }
  }
`;

interface StaffShiftsContextType {
  // Loading state for initial data fetch
  initialLoading: boolean;
  error?: string;
  // Map of staffId -> their shift for quick lookup
  lookup: Record<string, GetShifts_operativeViewer_staffShifts>;
  // Check if a staff member is currently clocked in
  isClockedIn: (staffId: string) => Promise<boolean>;
  // Clock in a staff member
  clockIn: (staffId: string) => Promise<void>;
  /**
   * Clock out a staff member. When the logged-in staff differs from the staff
   * being clocked out (e.g. a manager clocking out an employee) the
   * logged-in staff's id is sent as `actorStaffId` for the audit trail.
   * `timestamp` overrides "now" — used when back-dating a forgotten clock-out.
   */
  clockOut: (staffId: string, timestamp?: string) => Promise<void>;
  /** Refetch the shifts query. Returns the fresh shifts so callers don't have to wait for the next render. */
  refetch: () => Promise<GetShifts_operativeViewer_staffShifts[]>;
  /** Total minutes worked per staff id, summed across all of their shifts in the response. */
  workedMinutesTotal: Record<string, number>;
}

const StaffShiftsContext = createContext<StaffShiftsContextType | undefined>(undefined);

interface StaffShiftsProviderProps {
  children: ReactNode;
}

export const StaffShiftsProvider: React.FC<StaffShiftsProviderProps> = ({ children }) => {
  const { i18n } = useI18n();
  const logger = useLogger('staff-shifts');
  const dispatch = useDispatch();

  const venueId = useSelector((state) => state.venueId);
  const staffId = useSelector((state) => state.staff?.id);
  const companyStaff = useSelector((state) => state.companyStaff);

  const shiftsQuery = useQuery<GetShifts, GetShiftsVariables>(SHIFTS_QUERY, {
    skip: !staffId || !venueId, // Skip query if staff is not logged in
    variables: { venueId: venueId || '' },
  });

  const [clockInMutation] = useMutation<StaffClockIn, StaffClockInVariables>(CLOCK_IN);
  const [clockOutMutation] = useMutation<StaffClockOut, StaffClockOutVariables>(CLOCK_OUT);

  const generateEventMeta = () => ({
    createdAt: TimeManager.now().toISOString(),
    staffId,
    venueId,
  });

  const allShifts = shiftsQuery.data?.operativeViewer?.staffShifts ?? [];

  /** Each staff's most recent shift, keyed by staffId and ordered newest-first. */
  const lookup: Record<string, GetShifts_operativeViewer_staffShifts> = {};
  [...allShifts]
    .sort((a, b) => (b.clockedInAt ?? '').localeCompare(a.clockedInAt ?? ''))
    .forEach((shift) => {
      if (!lookup[shift.staffId]) {
        lookup[shift.staffId] = shift;
      }
    });

  /** Helper to fetch a staff's display name from the redux companyStaff list */
  const getStaff = (id: string) => companyStaff?.staffs.find((s) => s.id === id);

  /** Most recently started shift for a staff in a list (latest clockedInAt first). */
  const latestShiftFor = (shifts: GetShifts_operativeViewer_staffShifts[], id: string) =>
    shifts
      .filter((s) => s.staffId === id)
      .sort((a, b) => (b.clockedInAt ?? '').localeCompare(a.clockedInAt ?? ''))[0];

  const refetch = async (): Promise<GetShifts_operativeViewer_staffShifts[]> => {
    logger.info('Refetching staff shifts', { venueId });

    const MAX_RETRIES = 4;
    const RETRY_DELAY_MS = 4000;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await shiftsQuery.refetch();
        logger.info('Refetch succeeded', { attempt });
        return result.data?.operativeViewer?.staffShifts ?? [];
      } catch (err) {
        logger.warn('Refetch attempt failed', {
          attempt,
          remaining: MAX_RETRIES - attempt,
          reason: err instanceof Error ? err.message : String(err),
        });

        if (attempt === MAX_RETRIES) {
          logger.error('Refetch failed after all retries', { attempts: MAX_RETRIES });
          throw err;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }

    return [];
  };

  const isClockedIn = async (staffIdToCheck: string) => {
    const shiftFromCache = lookup[staffIdToCheck];

    /** Fast path: cache already shows an open shift. */
    if (shiftFromCache?.clockedInAt && !shiftFromCache?.clockedOutAt) {
      return true;
    }

    /** Read from the refetch result directly — the closure's `lookup` is stale until next render. */
    const freshShifts = await refetch();
    const latest = latestShiftFor(freshShifts, staffIdToCheck);

    return Boolean(latest?.clockedInAt && !latest?.clockedOutAt);
  };

  /**
   * Clock in a staff member. Retries up to 4 times with a 4 second delay
   * between attempts if the mutation fails.
   */
  const clockIn = async (staffIdToClockIn: string) => {
    if (!venueId) {
      logger.error('Cannot clock in without a venueId', { staffIdToClockIn });
      throw new Error('Missing venueId');
    }

    /** True when the logged-in staff is clocking themselves in. False when another staff (e.g. a manager) is performing the action on their behalf. */
    const staffClockThemself = staffId && staffId === staffIdToClockIn;

    const staff = getStaff(staffIdToClockIn);

    const meta = generateEventMeta();

    logger.info('Clocking in staff', { staffIdToClockIn, staffClockThemself, meta });

    const MAX_RETRIES = 4;
    const RETRY_DELAY_MS = 4000;
    let lastError: Error | null = null;
    /** Backend business rejection (success:false) — surfaced to the user, not retried. */
    let rejectionCode: string | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await clockInMutation({
          variables: {
            staffId: staffIdToClockIn,
            venueId,
            timestamp: meta.createdAt,
            ssn: staff?.ssn,
            staffName: staff?.name,
            /** Identifies the staff performing the action when it isn't the staff being clocked in (e.g. a manager). */
            actorStaffId: staffClockThemself ? undefined : staffId,
          },
        });

        const payload = result.data?.staffClockIn;

        /** Backend rejected the request — retrying won't help, surface the reason and stop. */
        if (!payload?.success) {
          rejectionCode = payload?.error ?? payload?.message ?? 'UNKNOWN_ERROR';
          break;
        }

        if (attempt > 0) {
          logger.info('Clock in succeeded after retry', { attempt, staffIdToClockIn });
        } else {
          logger.info('Clock in succeeded', { staffIdToClockIn });
        }

        dispatch(
          actions.addToast(
            i18n('StaffShifts.HasClockedIn', staff?.name ?? staffIdToClockIn),
            true,
          ),
        );

        refetch();

        return;
      } catch (err) {
        lastError = err as Error;

        if (attempt < MAX_RETRIES) {
          logger.warn('Clock in failed, retrying', {
            attempt: attempt + 1,
            maxRetries: MAX_RETRIES,
            errorMessage: lastError?.message,
            staffIdToClockIn,
          });

          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        }
      }
    }

    /** Backend rejected the request — show the reason and stop. */
    if (rejectionCode) {
      logger.error('Clock in rejected by backend', { staffIdToClockIn, rejectionCode });
      dispatch(
        actions.addToast(
          rejectionCode,
          true,
          null,
          true,
        ),
      );
      throw new Error(rejectionCode);
    }

    logger.error('Clock in failed after all retries', {
      maxRetries: MAX_RETRIES,
      errorMessage: lastError?.message,
      staffIdToClockIn,
    });

    throw lastError;
  };

  /**
   * Clock out a staff member. Retries up to 4 times with a 4 second delay
   * between attempts if the mutation fails.
   *
   * When the currently logged-in staff differs from the staff being clocked
   * out (e.g. a manager clocking out an employee) the logged-in staff's id is
   * sent as `actorStaffId` for the audit trail.
   */
  const clockOut = async (staffIdToClockOut: string, customTimestamp?: string) => {
    if (!venueId) {
      logger.error('Cannot clock out without a venueId', { staffIdToClockOut });
      throw new Error('Missing venueId');
    }

    /** True when the logged-in staff is clocking themselves out. False when another staff (e.g. a manager) is performing the action on their behalf. */
    const staffClockThemself = staffId && staffId === staffIdToClockOut;

    const staff = getStaff(staffIdToClockOut);

    /** Backend needs the open shift's id to close it. */
    const shiftId = lookup[staffIdToClockOut]?.id;
    if (!shiftId) {
      logger.error('Cannot clock out without an open shift', { staffIdToClockOut });
      throw new Error('Missing shiftId');
    }

    const meta = generateEventMeta();
    const timestamp = customTimestamp ?? meta.createdAt;

    logger.info('Clocking out staff', {
      staffIdToClockOut,
      shiftId,
      staffClockThemself,
      timestamp,
      isBackdated: Boolean(customTimestamp),
    });

    const MAX_RETRIES = 4;
    const RETRY_DELAY_MS = 4000;
    let lastError: Error | null = null;
    /** Backend business rejection (success:false) — surfaced to the user, not retried. */
    let rejectionCode: string | null = null;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await clockOutMutation({
          variables: {
            shiftId,
            staffId: staffIdToClockOut,
            venueId,
            timestamp,
            ssn: staff?.ssn,
            staffName: staff?.name,
            /** Identifies the staff performing the action when it isn't the staff being clocked out (e.g. a manager). */
            actorStaffId: staffClockThemself ? undefined : staffId,
          },
        });

        const payload = result.data?.staffClockOut;

        /** Backend rejected the request (e.g. timestamp outside live window) — retrying won't help, surface the reason and stop. */
        if (!payload?.success) {
          rejectionCode = payload?.error ?? payload?.message ?? 'UNKNOWN_ERROR';
          break;
        }

        if (attempt > 0) {
          logger.info('Clock out succeeded after retry', { attempt, staffIdToClockOut });
        } else {
          logger.info('Clock out succeeded', { staffIdToClockOut });
        }

        dispatch(
          actions.addToast(
            i18n('StaffShifts.HasClockedOut', staff?.name ?? staffIdToClockOut),
            true,
          ),
        );

        return;
      } catch (err) {
        lastError = err as Error;

        if (attempt < MAX_RETRIES) {
          logger.warn('Clock out failed, retrying', {
            attempt: attempt + 1,
            maxRetries: MAX_RETRIES,
            errorMessage: lastError?.message,
            staffIdToClockOut,
          });

          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        }
      }
    }

    /** Backend rejected the request — show the reason and stop. */
    if (rejectionCode) {
      logger.error('Clock out rejected by backend', { staffIdToClockOut, rejectionCode });
      dispatch(
        actions.addToast(
          rejectionCode,
          true,
          null,
          true,
        ),
      );
      throw new Error(rejectionCode);
    }

    logger.error('Clock out failed after all retries', {
      maxRetries: MAX_RETRIES,
      errorMessage: lastError?.message,
      staffIdToClockOut,
    });

    throw lastError;
  };

  /** Sum each shift's full duration; active shifts use "now" as the end. */
  const now = DateTime.fromJSDate(TimeManager.now());
  const workedMinutesTotal: Record<string, number> = {};

  allShifts.forEach((shift) => {
    if (!shift.clockedInAt) {
      return;
    }

    const start = DateTime.fromISO(shift.clockedInAt);
    const end = shift.clockedOutAt ? DateTime.fromISO(shift.clockedOutAt) : now;

    if (end <= start) {
      return;
    }

    const minutes = end.diff(start, 'minutes').minutes;
    workedMinutesTotal[shift.staffId] = (workedMinutesTotal[shift.staffId] || 0) + minutes;
  });

  const value: StaffShiftsContextType = {
    initialLoading: !shiftsQuery.data && shiftsQuery.loading,
    error: shiftsQuery.error?.message,
    lookup: lookup,
    workedMinutesTotal,
    isClockedIn,
    clockIn,
    clockOut,
    refetch,
  };

  return <StaffShiftsContext.Provider value={value}>{children}</StaffShiftsContext.Provider>;
};

export const useStaffShifts = (): StaffShiftsContextType => {
  const context = useContext(StaffShiftsContext);

  if (!context) {
    throw new Error('useStaffShifts must be used within a StaffShiftsProvider');
  }

  return context;
};

export default StaffShiftsProvider;
